import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgingReport, ReviewList } from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import { Divider } from '../common/Catalyst/divider';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../common/Catalyst/table';
import { Subheading } from '../common/Catalyst/heading';
import { Text } from '../common/Catalyst/text';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  agingSumsToBalance,
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
} from '../utils/billingHelpers';
import { caseStatus, reviewCondition } from './enumLabels';
import BillingJobsPanel from './BillingJobsPanel';

interface Props {
  hoaId: string;
  canRunJobs: boolean;
}

/**
 * What needs attention across the association.
 *
 * The review queue is **derived from state, not a stored inbox** —
 * every condition resolves through a real action with a ledger
 * consequence, so there is nothing to dismiss and no acknowledgement to
 * record. A count that goes to zero means the thing was actually dealt
 * with.
 */
const BillingOverview: React.FC<Props> = ({ hoaId, canRunJobs }) => {
  const navigate = useNavigate();
  const [aging, setAging] = useState<AgingReport | null>(null);
  const [review, setReview] = useState<ReviewList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [a, r] = await Promise.all([
        billingService.agingReport({
          hoaId,
          sort: 'balance',
          perPage: 200,
        }),
        billingService.reviewQueue({ hoaId, perPage: 100 }),
      ]);
      setAging(a.data);
      setReview(r.data);
    } catch (err) {
      console.error('Error loading overview:', err);
      setError(billingErrorMessage(err, "Couldn't load the overview."));
    } finally {
      setLoading(false);
    }
  }, [hoaId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !aging) return <LoadingSpinner />;

  const owing = (aging?.homes ?? []).filter((h) => h.balance !== '0.00');
  const counts = review?.counts ?? {};
  const raised = Object.entries(counts).filter(([, n]) => n > 0);
  const totals = aging?.totals;
  const footerCheck = totals
    ? agingSumsToBalance(
        {
          current: totals.current,
          days_1_30: totals.days_1_30,
          days_31_60: totals.days_31_60,
          days_61_90: totals.days_61_90,
          days_over_90: totals.days_over_90,
        },
        totals.balance
      )
    : null;

  return (
    <div>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="Couldn't load the overview"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline
          />
        </div>
      )}

      <Subheading>Needs a person</Subheading>
      <Text className="mt-1">
        Each of these is a real position on a real row, not a queue somebody has
        to tick off — they clear when the thing is actually dealt with.
      </Text>

      {raised.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 p-6">
          <Text>
            Nothing is waiting on a decision. All {Object.keys(counts).length}{' '}
            conditions are clear.
          </Text>
        </div>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {raised.map(([condition, n]) => (
            <div
              key={condition}
              className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-4"
            >
              <div className="text-2xl font-semibold text-zinc-900 dark:text-white">
                {n}
              </div>
              <div className="mt-1 text-sm font-medium text-zinc-900 dark:text-white">
                {reviewCondition.label(condition)}
              </div>
              <Text className="mt-1 text-xs">
                {reviewCondition.help(condition)}
              </Text>
            </div>
          ))}
        </div>
      )}

      {review && review.items.length > 0 && (
        <ul className="mt-4 space-y-2">
          {review.items.slice(0, 10).map((i) => (
            <li
              key={`${i.condition}-${i.entity_id}`}
              className="flex flex-wrap items-baseline gap-2 text-sm"
            >
              <Badge color={reviewCondition.color(i.condition)}>
                {reviewCondition.label(i.condition)}
              </Badge>
              <span className="text-zinc-900 dark:text-white">
                {i.home_label ? `${i.home_label} — ` : ''}
                {i.summary}
              </span>
              {i.amount && (
                <span className="font-medium text-zinc-900 dark:text-white">
                  {formatMoney(i.amount)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <Divider className="my-10" />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Subheading>Delinquency</Subheading>
          <Text className="mt-1">
            {aging
              ? `As of ${formatBillingDate(aging.as_of)}. ${owing.length} of ${aging.totals.homes} homes owe something.`
              : ''}
          </Text>
        </div>
        <Button
          plain
          onClick={() => navigate(`/hoa/${hoaId}/billing/collections`)}
        >
          Collections cases →
        </Button>
      </div>

      {totals && (
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-6">
          {[
            ['Owed', totals.balance],
            ['Not yet due', totals.current],
            ['1–30', totals.days_1_30],
            ['31–60', totals.days_31_60],
            ['61–90', totals.days_61_90],
            ['Over 90', totals.days_over_90],
          ].map(([label, value]) => (
            <div key={label}>
              <div className="text-xs text-zinc-500 dark:text-zinc-400">
                {label}
              </div>
              <div className="mt-1 font-medium text-zinc-900 dark:text-white">
                {formatMoney(value)}
              </div>
            </div>
          ))}
        </div>
      )}

      {footerCheck?.status === 'mismatch' && (
        <Text className="mt-3 text-amber-700 dark:text-amber-400">
          The buckets add up to {formatMoney(footerCheck.bucketTotal)} against a
          total of {formatMoney(footerCheck.balance)}. They are meant to be the
          same number.
        </Text>
      )}

      {owing.length === 0 ? (
        <div className="mt-4 rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 p-6">
          <Text>Nobody owes this association anything.</Text>
        </div>
      ) : (
        <Table dense className="mt-4">
          <TableHead>
            <TableRow>
              <TableHeader>Home</TableHeader>
              <TableHeader>Payer</TableHeader>
              <TableHeader className="text-right">Balance</TableHeader>
              <TableHeader className="text-right">Days</TableHeader>
              <TableHeader>Case</TableHeader>
              <TableHeader>HOLD</TableHeader>
              <TableHeader> </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {owing.map((h) => (
              <TableRow key={h.home_id}>
                <TableCell>{h.home_label ?? h.home_id}</TableCell>
                <TableCell>{h.payer_name ?? '—'}</TableCell>
                <TableCell className="text-right">
                  {formatMoney(h.balance)}
                </TableCell>
                <TableCell className="text-right">{h.days_past_due}</TableCell>
                <TableCell>
                  {h.case_status ? (
                    <Badge color={caseStatus.color(h.case_status)}>
                      {caseStatus.label(h.case_status)}
                    </Badge>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>
                  {h.hold ? <Badge color="red">HOLD</Badge> : '—'}
                </TableCell>
                <TableCell>
                  <Button
                    plain
                    onClick={() =>
                      navigate(`/hoa/${hoaId}/home/${h.home_id}/billing`)
                    }
                  >
                    Account
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {canRunJobs && (
        <>
          <Divider className="my-10" />
          <BillingJobsPanel hoaId={hoaId} />
        </>
      )}
    </div>
  );
};

export default BillingOverview;
